// Pure aggregation of on-chain Drip position accounts into the shapes the
// dashboard renders: a ranked depositor list and a per-reward-token breakdown.
// Kept dependency-free so it can be unit-tested without Solana/RPC.
//
// Values are normalised to SOL so positions in different LSTs are
// commensurable: 10 hSOL is worth more than 10 kySOL, and a leaderboard that
// compared raw token counts would rank them wrongly. Each position therefore
// carries its own solPerShare rather than the aggregator taking one rate.

export interface RawDripPosition {
  owner: string;
  /** Total shares (active + pending), already scaled to whole units (/1e9). */
  shares: number;
  rewardTokenMint: string;
  lastDepositEpoch: number;
  /** Which vault this position belongs to. */
  vaultId: string;
  /** SOL value of one share in this position's vault. */
  solPerShare: number;
}

export interface DepositorRow {
  owner: string;
  solValue: number;
  rewardTokenMint: string;
  lastDepositEpoch: number;
  vaultId: string;
  /** Share of total Drip SOL value, in percent. */
  vaultSharePct: number;
}

export interface TokenBreakdownRow {
  mint: string;
  depositorCount: number;
  solValue: number;
  vaultSharePct: number;
}

export interface DripDepositorStats {
  depositors: DepositorRow[];
  tokenBreakdown: TokenBreakdownRow[];
  totalSolValue: number;
  depositorCount: number;
  tokenCount: number;
}

// With no valid per-share value we fall back to raw shares (value ~= shares),
// which keeps rankings and proportions sane before vault state loads.
const toSolValue = (shares: number, solPerShare: number): number =>
  solPerShare > 0 ? shares * solPerShare : shares;

export const aggregateDripDepositors = (
  positions: RawDripPosition[]
): DripDepositorStats => {
  const active = positions.filter((position) => position.shares > 0);

  const depositorsUnranked = active.map((position) => ({
    owner: position.owner,
    solValue: toSolValue(position.shares, position.solPerShare),
    rewardTokenMint: position.rewardTokenMint,
    lastDepositEpoch: position.lastDepositEpoch,
    vaultId: position.vaultId,
  }));

  const totalSolValue = depositorsUnranked.reduce((sum, d) => sum + d.solValue, 0);
  const pct = (value: number) => (totalSolValue > 0 ? (value / totalSolValue) * 100 : 0);

  const depositors: DepositorRow[] = depositorsUnranked
    .map((d) => ({ ...d, vaultSharePct: pct(d.solValue) }))
    .sort((a, b) => b.solValue - a.solValue);

  const byMint = new Map<string, { depositorCount: number; solValue: number }>();
  for (const d of depositorsUnranked) {
    const entry = byMint.get(d.rewardTokenMint) ?? { depositorCount: 0, solValue: 0 };
    entry.depositorCount += 1;
    entry.solValue += d.solValue;
    byMint.set(d.rewardTokenMint, entry);
  }

  const tokenBreakdown: TokenBreakdownRow[] = Array.from(byMint.entries())
    .map(([mint, entry]) => ({
      mint,
      depositorCount: entry.depositorCount,
      solValue: entry.solValue,
      vaultSharePct: pct(entry.solValue),
    }))
    .sort((a, b) => b.solValue - a.solValue);

  return {
    depositors,
    tokenBreakdown,
    totalSolValue,
    depositorCount: depositors.length,
    tokenCount: tokenBreakdown.length,
  };
};
